import { SIM_POLICY_VERSION } from '@cruz/shared';
import type { ReactNode } from 'react';
import { Link, Notice, PageTitle } from './ui.tsx';

/*
 * Provisional legal pages of the simulator. To be reviewed by a lawyer before launch:
 * the holder's identity and contact are still placeholders.
 */

const HOLDER = '[Pendiente: nombre o razón social, NIF y dirección del responsable]';
const CONTACT = '[Pendiente: correo de contacto para privacidad]';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="font-serif text-2xl font-semibold text-purple dark:text-gold-300">{title}</h2>
      {children}
    </section>
  );
}

function Draft() {
  return (
    <Notice tone="warn">
      Texto provisional, pendiente de revisión por un profesional. Versión {SIM_POLICY_VERSION}.
    </Notice>
  );
}

export function PrivacyPage() {
  return (
    <article className="space-y-5 leading-relaxed">
      <PageTitle kicker="De cirio a hermano mayor">Política de privacidad</PageTitle>
      <Draft />
      <Section title="Quién trata tus datos">
        <p>
          {HOLDER}. Contacto: {CONTACT}.
        </p>
      </Section>
      <Section title="Qué datos guardamos">
        <ul className="list-disc space-y-1 pl-5">
          <li>Tu correo electrónico y tu apodo.</li>
          <li>Tu contraseña, cifrada (nunca la guardamos tal cual).</li>
          <li>Tu partida: la hermandad que inventas, tus decisiones y tus logros.</li>
          <li>
            Tus respuestas a los consentimientos: qué aceptaste, cuándo y con qué texto, como
            prueba.
          </li>
          <li>Las tarjetas que compartes (solo llevan tu apodo, nunca tu correo).</li>
        </ul>
        <p>No guardamos tu dirección IP en los registros del servidor.</p>
      </Section>
      <Section title="Para qué">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            Darte una cuenta y guardar tu partida para que juegues desde cualquier dispositivo
            (ejecución del contrato que aceptas al registrarte).
          </li>
          <li>
            Mandarte los correos de tu cuenta: verificación, cambio de contraseña y confirmación de
            borrado (ejecución del contrato).
          </li>
          <li>
            Solo si lo marcas: mandarte novedades del juego, de Cruz de IA y del proyecto
            (consentimiento, que puedes retirar cuando quieras).
          </li>
          <li>Proteger el servicio frente a abusos (interés legítimo).</li>
        </ul>
      </Section>
      <Section title="Con quién se comparten">
        <ul className="list-disc space-y-1 pl-5">
          <li>El proveedor de alojamiento del servidor y la base de datos.</li>
          <li>El proveedor de envío de correos, solo para mandarte los mensajes.</li>
          <li>
            OpenAI, que escribe los textos del juego y revisa los nombres que inventas. Le llegan
            los textos de la partida, nunca tu correo ni tu contraseña.
          </li>
          <li>
            Si aceptas las novedades, la herramienta de correo que usemos para mandarlas (tu correo
            y tu apodo).
          </li>
        </ul>
      </Section>
      <Section title="Cuánto tiempo">
        <p>
          Mientras tengas la cuenta. Si la borras, se borran tu cuenta, tus partidas y tus tarjetas
          en ese momento. Las copias de seguridad se renuevan en un plazo máximo de 30 días.
        </p>
      </Section>
      <Section title="Tus derechos">
        <p>
          Puedes acceder a tus datos, corregirlos, borrarlos, oponerte a su uso, limitarlo o pedir
          una copia escribiendo a {CONTACT}. Desde los ajustes de tu cuenta puedes darte de baja de
          las novedades y borrar la cuenta tú mismo. Si crees que no hemos tratado bien tus datos,
          puedes reclamar ante la Agencia Española de Protección de Datos (aepd.es).
        </p>
      </Section>
      <Section title="Cookies">
        <p>
          Solo usamos una cookie técnica para mantener tu sesión abierta, y el almacenamiento del
          navegador para recordar el tema claro u oscuro. No hay cookies de publicidad ni de
          analítica de terceros.
        </p>
      </Section>
      <p>
        <Link to="/simulador/condiciones">Condiciones de uso</Link>
      </p>
    </article>
  );
}

export function TermsPage() {
  return (
    <article className="space-y-5 leading-relaxed">
      <PageTitle kicker="De cirio a hermano mayor">Condiciones de uso</PageTitle>
      <Draft />
      <Section title="El juego">
        <p>
          «De cirio a hermano mayor» es un juego gratuito de Cruz de IA ({HOLDER}). Es ficción: las
          hermandades, los personajes y lo que les pasa son inventados. No tiene relación con
          ninguna hermandad, cofradía ni institución real.
        </p>
        <p>
          Los textos los escribe una inteligencia artificial y se revisan de forma automática. Aun
          así pueden contener errores; si ves algo que no debería estar, avísanos.
        </p>
      </Section>
      <Section title="Tu cuenta">
        <ul className="list-disc space-y-1 pl-5">
          <li>Tienes que tener al menos 14 años.</li>
          <li>
            Una cuenta por persona. Cuida tu contraseña: eres responsable de lo que se haga con
            ella.
          </li>
          <li>Tu apodo y tu hermandad se pueden ver en las tarjetas que compartas.</li>
        </ul>
      </Section>
      <Section title="Respeto">
        <p>
          Nos reímos del mundo cofrade, nunca de la fe. No se permiten apodos ni nombres de
          hermandad ofensivos, que se burlen de lo sagrado o que copien hermandades reales. Podemos
          cambiarlos o cerrar cuentas que no respeten estas normas.
        </p>
      </Section>
      <Section title="El servicio">
        <p>
          Intentamos que el juego funcione siempre, pero puede haber cortes o cambios. Podemos
          ajustar las reglas del juego para mejorarlo. Puedes dejar de jugar y borrar tu cuenta
          cuando quieras desde los ajustes.
        </p>
      </Section>
      <p>
        <Link to="/simulador/privacidad">Política de privacidad</Link>
      </p>
    </article>
  );
}
